/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Persona & AI Model Selectors (Task 10.3.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  SYSTEM_CHAT_PERSONAS,
  CHAT_AI_MODELS,
  type EnrichedChatPersona,
} from '../../data/chat-personas.catalog';
import { useI18n } from '../../hooks/useI18n';
import {
  Sparkles,
  Code2,
  Megaphone,
  BookOpen,
  ShieldCheck,
  TrendingUp,
  Bot,
  Cpu,
  ChevronDown,
  Check,
  X,
  Coins,
  Search,
} from 'lucide-react';

// Icon mapper for system personas
export const getPersonaIcon = (iconName: string) => {
  switch (iconName) {
    case 'Sparkles':
      return Sparkles;
    case 'Code2':
      return Code2;
    case 'Megaphone':
      return Megaphone;
    case 'BookOpen':
      return BookOpen;
    case 'ShieldCheck':
      return ShieldCheck;
    case 'TrendingUp':
      return TrendingUp;
    default:
      return Bot;
  }
};

/**
 * 1. AI Model Selector Dropdown
 */
interface ModelSelectorDropdownProps {
  activeModelCode: string;
  onSelectModel: (code: string) => void;
  className?: string;
}

export const ModelSelectorDropdown: React.FC<ModelSelectorDropdownProps> = ({
  activeModelCode,
  onSelectModel,
  className = '',
}) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeModel =
    CHAT_AI_MODELS.find((m) => m.code === activeModelCode) || CHAT_AI_MODELS[0];

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-surface-border bg-surface-elevated hover:bg-muted/80 text-foreground text-xs font-medium transition-all shadow-sm"
        aria-expanded={isOpen}
      >
        <Cpu className="w-3.5 h-3.5 text-primary" />
        <span className="font-semibold">{activeModel.name}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-bold hidden sm:inline-flex">
          {activeModel.creditCost} {t('common.credits')}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-foreground-muted transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-72 sm:w-80 rounded-2xl border border-surface-border bg-surface p-2 shadow-2xl z-50 animate-in fade-in zoom-in-95 duration-150">
          <div className="px-3 py-2 border-b border-surface-border/60 mb-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-foreground-muted">
              {t('chat.selectAiModel')}
            </span>
          </div>

          <div className="space-y-1">
            {CHAT_AI_MODELS.map((model) => {
              const isSelected = model.code === activeModelCode;
              return (
                <button
                  key={model.code}
                  type="button"
                  onClick={() => {
                    onSelectModel(model.code);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-start justify-between gap-3 p-2.5 rounded-xl text-left transition-all ${
                    isSelected
                      ? 'bg-primary/10 border border-primary/20 text-foreground'
                      : 'hover:bg-muted/60 text-foreground-muted hover:text-foreground'
                  }`}
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-xs text-foreground">
                        {model.name}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.2 rounded-full font-bold bg-accent/15 text-accent border border-accent/20">
                        {model.badge}
                      </span>
                    </div>
                    <p className="text-[11px] text-foreground-muted line-clamp-1 leading-relaxed">
                      {model.description}
                    </p>
                    <div className="flex items-center gap-2 text-[10px] text-foreground-muted">
                      <span>{t('chat.providedBy', { provider: model.providerLabel })}</span>
                      <span>•</span>
                      <span className="inline-flex items-center gap-1 font-semibold text-primary">
                        <Coins className="w-3 h-3" />
                        {t('chat.creditsPerResponse', { credits: model.creditCost })}
                      </span>
                    </div>
                  </div>

                  {isSelected && (
                    <Check className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * 2. Persona Selector Modal & Explorer
 */
interface PersonaSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  activePersonaId: string;
  onSelectPersona: (persona: EnrichedChatPersona) => void;
}

export const PersonaSelectorModal: React.FC<PersonaSelectorModalProps> = ({
  isOpen,
  onClose,
  activePersonaId,
  onSelectPersona,
}) => {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  if (!isOpen) return null;

  const categories = ['all', ...Array.from(new Set(SYSTEM_CHAT_PERSONAS.map((p) => p.category)))];

  const filteredPersonas = SYSTEM_CHAT_PERSONAS.filter((p) => {
    const matchesSearch =
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.roleDescription.toLowerCase().includes(search.toLowerCase()) ||
      p.category.toLowerCase().includes(search.toLowerCase());
    const matchesCategory =
      selectedCategory === 'all' || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative w-full max-w-4xl max-h-[90vh] flex flex-col rounded-3xl border border-surface-border bg-surface shadow-2xl overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-surface-border bg-surface-elevated/40">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-primary/15 text-primary flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-extrabold text-foreground">
                {t('chat.personaLibraryTitle')}
              </h2>
              <p className="text-xs text-foreground-muted">
                {t('chat.personaLibrarySubtitle')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-foreground-muted hover:text-foreground hover:bg-muted/80 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filter Bar & Search */}
        <div className="p-4 border-b border-surface-border bg-surface space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 text-foreground-muted absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('chat.searchPersonaPlaceholder')}
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs text-foreground placeholder:text-foreground-muted/60 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
            />
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none text-xs">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                  selectedCategory === cat
                    ? 'bg-primary text-primary-fg font-semibold shadow-sm'
                    : 'bg-surface-elevated text-foreground-muted hover:text-foreground border border-surface-border'
                }`}
              >
                {cat === 'all' ? t('chat.allRoles') : cat}
              </button>
            ))}
          </div>
        </div>

        {/* Personas Grid */}
        <div className="p-6 overflow-y-auto space-y-4 max-h-[60vh]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredPersonas.map((persona) => {
              const isCurrent = persona.id === activePersonaId;
              const PersonaIcon = getPersonaIcon(persona.iconName);
              const recommendedModelObj = CHAT_AI_MODELS.find(
                (m) => m.code === persona.recommendedModel
              );

              return (
                <div
                  key={persona.id}
                  onClick={() => {
                    onSelectPersona(persona);
                    onClose();
                  }}
                  className={`group relative p-5 rounded-2xl border cursor-pointer transition-all duration-200 flex flex-col justify-between space-y-4 ${
                    isCurrent
                      ? 'border-primary bg-primary/5 ring-2 ring-primary/20 shadow-lg'
                      : 'border-surface-border bg-surface-elevated/40 hover:border-primary/50 hover:bg-surface-elevated shadow-sm'
                  }`}
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 border border-primary/30 flex items-center justify-center text-primary flex-shrink-0 group-hover:scale-105 transition-transform">
                          <PersonaIcon className="w-6 h-6" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-heading font-bold text-sm text-foreground">
                              {persona.name}
                            </h3>
                            {isCurrent && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary text-primary-fg font-bold">
                                {t('chat.currentlyUsing')}
                              </span>
                            )}
                          </div>
                          <span className="text-[11px] font-semibold text-primary">
                            {persona.category}
                          </span>
                        </div>
                      </div>

                      <span className="text-[10px] px-2 py-0.5 rounded-lg border border-surface-border bg-surface font-mono text-foreground-muted">
                        {t('chat.recommendedModel')}: {recommendedModelObj?.name || persona.recommendedModel}
                      </span>
                    </div>

                    <p className="text-xs text-foreground-muted leading-relaxed">
                      {persona.roleDescription}
                    </p>

                    {/* Starter prompts preview */}
                    <div className="space-y-1.5 pt-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-foreground-muted/80">
                        {t('chat.starterQuestionsLabel', { count: persona.starterQuestions.length })}:
                      </span>
                      <div className="space-y-1">
                        {persona.starterQuestions.slice(0, 2).map((q, idx) => (
                          <div
                            key={idx}
                            className="text-[11px] text-foreground-muted/90 bg-surface/70 px-2.5 py-1 rounded-lg border border-surface-border/50 truncate"
                          >
                            &ldquo;{q}&rdquo;
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-surface-border/60 text-xs">
                    <span className="text-[11px] text-foreground-muted">
                      {t('chat.startersReady', { count: persona.starterQuestions.length })}
                    </span>
                    <button
                      type="button"
                      className={`px-3 py-1.5 rounded-xl font-semibold transition-all ${
                        isCurrent
                          ? 'bg-primary text-primary-fg'
                          : 'bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-fg'
                      }`}
                    >
                      {isCurrent ? t('common.active') : t('chat.selectThisPersona')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
